/**
 * UpdateModal.tsx — the in-app updater (the version checker's action surface).
 *
 * Shows what drifted — the frontend (baked vs served manifest hash) and the
 * stack's api/jupyter containers (live digests vs the release manifest) —
 * and applies the update:
 * - frontend drift            → Reload (the deploy already serves the new
 *                               bundle; index.html is uncached).
 * - stack drift, orchestrated → POST /api/system/update starts the detached
 *   updater container (docker compose pull && up -d from OUTSIDE the dying
 *   api). This modal polls the status endpoint while the old api lives,
 *   then /health until the new one is up, then reloads. Running tasks are
 *   preserved (the exit sweep is skipped; the new api boot-adopts them).
 * - stack drift, pull-only    → docker pull of the stack images + the
 *   one-liner to restart the stack manually (layouts the updater can't
 *   verify, or a preference).
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowClockwise, DownloadSimple } from '@phosphor-icons/react';

import { useAuth } from '../auth/AuthContext';
import { httpOriginOf } from '../connect/httpOrigin';
import { useVersionCheck } from '../version/useVersionCheck';
import { digestsDiffer, bakedManifest, shortHash } from '../version/manifest';
import { Modal } from './Modal';

type Phase =
  | 'idle'
  | 'pulling'        // pull-only flow running
  | 'orchestrating'  // updater container started, old api still alive
  | 'waiting-api'    // the old api is going down — waiting for the new one
  | 'reloading'      // new api up — about to reload the page
  | 'done'           // pull-only finished (manual restart needed)
  | 'error';

interface UpdateStatus {
  running?: boolean;
  mode?: string | null;
  phase?: string | null;
  error?: string | null;
  output?: string | null;
  updater?: { running?: boolean; exitCode?: number | null; output?: string } | null;
}

const OVERALL_BUDGET_MS = 6 * 60_000;

async function errorText(r: Response): Promise<string> {
  try {
    const j = (await r.json()) as { detail?: unknown };
    if (typeof j?.detail === 'string') return j.detail;
  } catch { /* ignore */ }
  return `request failed (${r.status})`;
}

const sleep = (ms: number): Promise<void> => new Promise((res) => setTimeout(res, ms));

export function UpdateModal({ onClose }: { onClose: () => void }): JSX.Element {
  const auth = useAuth();
  const backendOrigin = auth.wsUrl ? httpOriginOf(auth.wsUrl, '') : null;
  const token = auth.token;
  const { served, live, frontendUpdate, stackUpdate, check } = useVersionCheck();
  const [phase, setPhase] = useState<Phase>('idle');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const cancelled = useRef(false);

  useEffect(() => (): void => {
    cancelled.current = true;
  }, []);

  const authHeaders = (): Record<string, string> =>
    token ? { Authorization: `Bearer ${token}` } : {};

  const pollStatus = (): Promise<UpdateStatus | null> =>
    backendOrigin
      ? fetch(`${backendOrigin}/api/system/update`, { headers: authHeaders() })
          .then(async (r) => (r.ok ? ((await r.json()) as UpdateStatus) : null))
          .catch(() => null)
      : Promise.resolve(null);

  const runUpdate = async (pullOnly: boolean): Promise<void> => {
    if (!backendOrigin) return;
    const deadline = Date.now() + OVERALL_BUDGET_MS;
    setError(null);
    setOutput('');
    setPhase(pullOnly ? 'pulling' : 'orchestrating');
    const headers = { ...authHeaders(), 'Content-Type': 'application/json' };
    try {
      const res = await fetch(`${backendOrigin}/api/system/update`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ pullOnly }),
      });
      // 409 (already running) falls through to polling; other errors stop.
      if (!res.ok && res.status !== 409) throw new Error(await errorText(res));

      if (pullOnly) {
        for (;;) {
          if (cancelled.current) return;
          const st = await pollStatus();
          if (st?.phase === 'error') throw new Error(st.error || 'update failed');
          if (st && st.running === false) {
            setOutput(st.output || '');
            setPhase('done');
            void check();
            return;
          }
          await sleep(2000);
          if (Date.now() > deadline) throw new Error('the pull timed out');
        }
      }

      // Orchestrated: poll the status while THIS api is alive; consecutive
      // network failures mean it's being recreated → wait for the new one.
      let misses = 0;
      for (;;) {
        if (cancelled.current) return;
        const st = await pollStatus();
        if (st === null) {
          misses += 1;
          if (misses >= 2) break;
        } else {
          misses = 0;
          if (st.phase === 'error') throw new Error(st.error || 'update failed');
          setOutput(st.updater?.output || st.output || '');
        }
        await sleep(2000);
        if (Date.now() > deadline) throw new Error('the update timed out');
      }
      setPhase('waiting-api');
      for (;;) {
        if (cancelled.current) return;
        const ok = await fetch(`${backendOrigin}/health`)
          .then((r) => r.ok)
          .catch(() => false);
        if (ok) {
          setPhase('reloading');
          void check();
          await sleep(1200);
          window.location.reload();
          return;
        }
        await sleep(3000);
        if (Date.now() > deadline) {
          throw new Error(
            'the api did not come back in time — check ' +
            '`docker logs vski-distillery-updater` on the host',
          );
        }
      }
    } catch (e) {
      if (!cancelled.current) {
        setError((e as Error).message);
        setPhase('error');
      }
    }
  };

  const busy = phase === 'pulling' || phase === 'orchestrating' || phase === 'waiting-api';
  const canOrchestrate = live?.canSelfUpdate === true;
  const apiOutdated = !!served && !!live?.api && digestsDiffer(served.images.api, live.api.digest);
  const jupOutdated =
    !!served && !!live?.jupyter && digestsDiffer(served.images.jupyter, live.jupyter.digest);

  const Row = ({
    name, current, released, outdated,
  }: {
    name: string; current: string; released: string; outdated: boolean;
  }): JSX.Element => (
    <div className="services-row">
      <div className="services-row-main">
        <div className="services-row-name">
          {name}
          {outdated && <span className="agents-fence read-only">outdated</span>}
        </div>
        <div className="services-row-meta">
          running <code>{current}</code> · release <code>{released}</code>
        </div>
      </div>
    </div>
  );

  return (
    <Modal title="Update" onClose={busy ? () => { /* keep the flow visible */ } : onClose}>
      <div className="services-list">
        {error && <div className="settings-conn-status err">{error}</div>}

        {phase === 'done' && (
          <div className="param-hint">
            Images pulled. Apply them with a stack restart on the host:
            <code style={{ display: 'block', marginTop: 6 }}>
              docker compose pull &amp;&amp; docker compose up -d
            </code>
          </div>
        )}
        {phase === 'reloading' && <div className="param-hint">Back up — reloading…</div>}
        {phase === 'waiting-api' && (
          <div className="param-hint">Recreating the stack — waiting for the api…</div>
        )}

        <Row
          name="Frontend"
          current={shortHash(bakedManifest?.frontend.sourceMd5)}
          released={shortHash(served?.frontend.sourceMd5)}
          outdated={frontendUpdate === true}
        />
        {live?.api && (
          <Row
            name="api"
            current={shortHash(live.api.digest)}
            released={shortHash(served?.images.api)}
            outdated={apiOutdated}
          />
        )}
        {live?.jupyter && (
          <Row
            name="jupyter"
            current={shortHash(live.jupyter.digest)}
            released={shortHash(served?.images.jupyter)}
            outdated={jupOutdated}
          />
        )}
        {live && live.api?.source === 'local' && (
          <div className="param-hint">
            The stack's images were built locally (no registry digest) — the
            release-digest check doesn't apply.
          </div>
        )}
        {stackUpdate === null && live === null && backendOrigin && (
          <div className="param-hint">No backend version info (unreachable or not docker).</div>
        )}

        {(phase === 'orchestrating' || phase === 'pulling') && output && (
          <pre className="update-output">{output.slice(-1500)}</pre>
        )}
      </div>
      <div className="modal-footer-row">
        <span className="param-hint">
          {served ? `release ${served.version}` : 'release unknown'}
        </span>
        <span className="flex-spacer" />
        {frontendUpdate === true && phase !== 'reloading' && (
          <button
            type="button"
            className="btn"
            disabled={busy}
            onClick={() => window.location.reload()}
          >
            <ArrowClockwise size={12} aria-hidden /> Reload frontend
          </button>
        )}
        {stackUpdate === true && phase === 'idle' && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void runUpdate(false)}
            disabled={!backendOrigin || !canOrchestrate}
            title={
              canOrchestrate
                ? 'Pull the new images and recreate the stack (running tasks are preserved)'
                : 'This stack cannot be updated in-app — use pull-only'
            }
          >
            <DownloadSimple size={12} aria-hidden /> Update &amp; restart
          </button>
        )}
        {stackUpdate === true && phase === 'idle' && !canOrchestrate && (
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void runUpdate(true)}
            disabled={!backendOrigin}
          >
            <DownloadSimple size={12} aria-hidden /> Pull images
          </button>
        )}
        {phase === 'error' && stackUpdate === true && (
          <button
            type="button"
            className="btn"
            onClick={() => void runUpdate(canOrchestrate ? false : true)}
          >
            Retry
          </button>
        )}
        <button type="button" className="btn" onClick={onClose} disabled={phase === 'reloading'}>
          Close
        </button>
      </div>
    </Modal>
  );
}
